import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable no-control-regex */
import { forwardRef, useEffect, useMemo, useReducer, useRef } from 'react';
import clsx from 'clsx';
import { Gapped } from '@skbkontur/react-ui';
import { ValidationContainer } from '@skbkontur/react-ui-validations';
import { checkDateInRange, checkNotEmpty, validateComboBox } from '../../helpers/validators';
import { DocumentIssuerField } from './components/DocumentIssuerField/DocumentIssuerField';
import { checkIssuerMaxLegth } from './components/DocumentIssuerField/DocumentIssuerField.validation';
import { DocumentIssuerUnitCodeField, validateDocumentIssuerUnitCode } from './components/DocumentIssuerUnitCodeField';
import { DocumentNumberField, validateDocumentNumber } from './components/DocumentNumberField';
import { DocumentPeriodField } from './components/DocumentPeriodFields/DocumentPeriodField';
import { DocumentTypeField } from './components/DocumentTypeField';
import { DriverCategoryField } from './components/DriverCategoryField';
import { reducer } from './IdentityDocumentForm.reducer';
import classes from './IdentityDocumentForm.module.css';
const defaultState = {
    id: '',
    code: '',
    number: '',
    issuer: '',
    issuerUnitCode: '',
    validityPeriodBegin: '',
    validityPeriodEnd: '',
    driversCategories: [],
};
export const IdentityDocumentForm = forwardRef(({ initialState, onSubmit, documentReferences, disabled, onCompareWithInitial, trackSaveFail, driverLicenseCategories, }, ref) => {
    const [state, dispatch] = useReducer(reducer, defaultState);
    const refValidationContainer = useRef(null);
    const handleSubmit = async (e) => {
        e.preventDefault();
        const isValid = (await refValidationContainer.current?.validate()) || false;
        if (!onSubmit || !isValid) {
            trackSaveFail && trackSaveFail();
            return;
        }
        const trimmedIssuer = state.issuer
            .replace(/[\u0000-\u0008,\u000A-\u001F,\u007F-\u00A0]+/g, ' ')
            .replace(/ +(?= )/g, '')
            .trim();
        onSubmit({ ...state, issuer: trimmedIssuer });
    };
    useEffect(() => {
        dispatch({ type: 'changeState', payload: initialState || defaultState });
    }, [initialState]);
    useEffect(() => {
        if (!onCompareWithInitial) {
            return;
        }
        onCompareWithInitial(initialState, state);
    }, [initialState, state]);
    const typeValue = useMemo(() => {
        return {
            code: state.code,
            name: documentReferences.find((el) => el.code === state.code)?.name || '',
        };
    }, [documentReferences, state.code]);
    return (_jsx("form", { onSubmit: handleSubmit, ref: ref, children: _jsx(ValidationContainer, { ref: refValidationContainer, children: _jsxs(Gapped, { vertical: true, gap: 16, children: [_jsx(DocumentTypeField, { documentsList: documentReferences, onValueChange: (v) => dispatch({ type: 'changeCode', payload: v.code }), value: typeValue, disabled: disabled, labelClassName: classes.label, validationInfo: checkNotEmpty({ value: state.code }) ||
                            validateComboBox({ value: typeValue, items: documentReferences }) }), _jsx(DocumentNumberField, { onValueChange: (v) => dispatch({ type: 'changeNumber', payload: v }), value: state.number, documentType: state.code, disabled: disabled, labelClassName: classes.label, validationInfo: validateDocumentNumber({
                            value: state.number,
                            required: true,
                            documentType: state.code,
                        }) }), _jsx(DocumentIssuerField, { labelClassName: clsx(classes.label, classes.textareaLabel), value: state.issuer, disabled: disabled, onValueChange: (v) => dispatch({ type: 'changeIssuer', payload: v }), validationInfo: checkIssuerMaxLegth({ value: state.issuer }) }), _jsx(DocumentIssuerUnitCodeField, { "data-tid": "issuerUnitCodeInput", onValueChange: (v) => dispatch({ type: 'changeIssuerUnitCode', payload: v }), value: state.issuerUnitCode, labelClassName: classes.label, documentType: state.code, disabled: disabled, validationInfo: validateDocumentIssuerUnitCode({
                            value: state.issuerUnitCode,
                            documentType: state.code,
                        }) }), _jsx(DocumentPeriodField, { labelClassName: classes.label, validationInfo: checkDateInRange({
                            value: state.validityPeriodBegin,
                            type: 'lostfocus',
                            maxDate: state.validityPeriodEnd,
                        }), value: state.validityPeriodBegin, disabled: disabled, onValueChange: (v) => dispatch({ type: 'changeValidityPeriodBegin', payload: v }), type: "begin" }), _jsx(DocumentPeriodField, { labelClassName: classes.label, validationInfo: checkDateInRange({
                            value: state.validityPeriodEnd,
                            type: 'lostfocus',
                            minDate: state.validityPeriodBegin,
                        }), value: state.validityPeriodEnd, disabled: disabled, onValueChange: (v) => dispatch({ type: 'changeValidityPeriodEnd', payload: v }), type: "end" }), state.code === '40' && (_jsx(DriverCategoryField, { driverLicenseCategories: driverLicenseCategories, value: state.driversCategories, validationInfo: checkNotEmpty({ value: state.driversCategories, type: 'lostfocus' }), onValueChange: (v) => dispatch({ type: 'changeDriversCategories', payload: v }), disabled: disabled }))] }) }) }));
});
IdentityDocumentForm.displayName = 'IdentityDocumentForm';
